// MD2 PRO
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';
import Icon from '@mui/material/Icon';
import { useState, useEffect, useCallback, useMemo } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    InputAdornment,
    IconButton,
    List,
    ListItem,
    ListItemText,
    CircularProgress,
} from '@mui/material';
import {
    Search as SearchIcon,
    Close as CloseIcon,
    Add as AddIcon,
    Edit as EditIcon,
    Folder as FolderIcon,
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { searchProjects } from 'services/api/project';
import { updateProduction } from 'services/api/production';
import SummaryCard from 'layouts/production/Components/AaroComponents/SummaryCard';

// Debounce function
const useDebounce = (value, delay) => {
    const [debouncedValue, setDebouncedValue] = useState(value);

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedValue(value);
        }, delay);

        return () => {
            clearTimeout(handler);
        };
    }, [value, delay]);

    return debouncedValue;
};

// Format currency function
const formatCurrency = (amount, currency = 'TRY') => {
    return new Intl.NumberFormat('tr-TR', {
        style: 'currency',
        currency: currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(amount);
};

/**
 * ProjectsSection Component
 * Displays the associated project for a production and allows changing the association
 */
function ProjectsSection({ production, setValue, open = false, onClose = null, showRelateButton = true }) {
    // Component state
    const [openAssociateDialog, setOpenAssociateDialog] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [isSearching, setIsSearching] = useState(false);
    const [isUpdating, setIsUpdating] = useState(false);
    const [page] = useState(1);
    const [limit] = useState(10);

    // Access the single project relationship
    const relatedProject = production?.project;

    // Debounce search query to prevent excessive API calls
    const debouncedSearchQuery = useDebounce(searchQuery, 500);

    // Calculate financial data from the project demand
    const { mainTotal, groupsTotal, currency, componentTotals, groupTotals } = useMemo(() => {
        // Get demand data from related project
        const demand = relatedProject?.demand;

        if (!demand)
            return {
                mainTotal: 0,
                groupsTotal: 0,
                totalAmount: 0,
                currency: 'TRY',
                componentTotals: {},
                groupTotals: {},
            };

        // Get currency from demand
        const currency = demand.demandCurrency || 'TRY';

        // Arrays to process
        const arrayTypes = ['uKasa', 'uPervaz', 'kasa', 'pervaz', 'kanat', 'diger'];

        // Process all items in demand
        let mainTotal = 0; // Total for main components (not in groups)
        let groupsTotal = 0; // Total for all groups

        // Track component totals for summary
        const componentTotals = {
            Kasa: { count: 0, amount: 0 },
            uKasa: { count: 0, amount: 0 },
            Pervaz: { count: 0, amount: 0 },
            uPervaz: { count: 0, amount: 0 },
            Kanat: { count: 0, amount: 0 },
            Diğer: { count: 0, amount: 0 },
        };

        // Track group totals
        const groupTotals = {};

        // Process main demand arrays
        arrayTypes.forEach((type) => {
            if (Array.isArray(demand[type])) {
                demand[type].forEach((item) => {
                    if (item && typeof item.verilenFiyat === 'number' && typeof item.miktar === 'number') {
                        const price = item.verilenFiyat;
                        const quantity = item.miktar;
                        const discountRate = item.indirimOrani || 0;

                        // Calculate item total with discount
                        // If discount rate is 5, divide by 1.05
                        const discountFactor = 1 + discountRate / 100;
                        const itemTotal = (price * quantity) / discountFactor;

                        mainTotal += itemTotal; // Add to main total

                        // Update component totals
                        if (type === 'kasa') {
                            componentTotals['Kasa'].count += quantity;
                            componentTotals['Kasa'].amount += itemTotal;
                        } else if (type === 'uKasa') {
                            componentTotals['uKasa'].count += quantity;
                            componentTotals['uKasa'].amount += itemTotal;
                        } else if (type === 'pervaz') {
                            componentTotals['Pervaz'].count += quantity;
                            componentTotals['Pervaz'].amount += itemTotal;
                        } else if (type === 'uPervaz') {
                            componentTotals['uPervaz'].count += quantity;
                            componentTotals['uPervaz'].amount += itemTotal;
                        } else if (type === 'kanat') {
                            componentTotals['Kanat'].count += quantity;
                            componentTotals['Kanat'].amount += itemTotal;
                        } else if (type === 'diger') {
                            componentTotals['Diğer'].count += quantity;
                            componentTotals['Diğer'].amount += itemTotal;
                        }
                    }
                });
            }
        });

        // Process groups if they exist
        if (demand.groups && Array.isArray(demand.groups)) {
            demand.groups.forEach((group) => {
                const groupName = group.groupName || 'Isimsiz Grup';
                const groupId = group._id;

                if (!groupTotals[groupId]) {
                    groupTotals[groupId] = {
                        name: groupName,
                        amount: 0,
                        components: {
                            Kasa: { count: 0, amount: 0 },
                            uKasa: { count: 0, amount: 0 },
                            Pervaz: { count: 0, amount: 0 },
                            uPervaz: { count: 0, amount: 0 },
                            Kanat: { count: 0, amount: 0 },
                            Diğer: { count: 0, amount: 0 },
                        },
                    };
                }

                arrayTypes.forEach((type) => {
                    if (Array.isArray(group[type])) {
                        group[type].forEach((groupItem) => {
                            if (groupItem && groupItem.ref) {
                                const item = groupItem.ref;
                                const quantity = groupItem.quantity || item.miktar || 0;

                                if (typeof item.verilenFiyat === 'number') {
                                    const price = item.verilenFiyat;
                                    const discountRate = item.indirimOrani || 0;

                                    // Calculate item total with discount
                                    const discountFactor = 1 + discountRate / 100;
                                    const itemTotal = (price * quantity) / discountFactor;

                                    groupsTotal += itemTotal; // Add to groups total, not to main total
                                    groupTotals[groupId].amount += itemTotal;

                                    // Only update the group component counts, not the main component counts
                                    if (type === 'kasa') {
                                        groupTotals[groupId].components['Kasa'].count += quantity;
                                        groupTotals[groupId].components['Kasa'].amount += itemTotal;
                                    } else if (type === 'uKasa') {
                                        groupTotals[groupId].components['uKasa'].count += quantity;
                                        groupTotals[groupId].components['uKasa'].amount += itemTotal;
                                    } else if (type === 'pervaz') {
                                        groupTotals[groupId].components['Pervaz'].count += quantity;
                                        groupTotals[groupId].components['Pervaz'].amount += itemTotal;
                                    } else if (type === 'uPervaz') {
                                        groupTotals[groupId].components['uPervaz'].count += quantity;
                                        groupTotals[groupId].components['uPervaz'].amount += itemTotal;
                                    } else if (type === 'kanat') {
                                        groupTotals[groupId].components['Kanat'].count += quantity;
                                        groupTotals[groupId].components['Kanat'].amount += itemTotal;
                                    } else if (type === 'diger') {
                                        groupTotals[groupId].components['Diğer'].count += quantity;
                                        groupTotals[groupId].components['Diğer'].amount += itemTotal;
                                    }
                                }
                            }
                        });
                    }
                });
            });
        }

        // Format the values to 2 decimal places
        return {
            mainTotal: parseFloat(mainTotal.toFixed(2)),
            groupsTotal: parseFloat(groupsTotal.toFixed(2)),
            totalAmount: parseFloat((mainTotal + groupsTotal).toFixed(2)),
            currency,
            componentTotals,
            groupTotals,
        };
    }, [relatedProject]);

    // Create summary items for the card
    const summaryItems = useMemo(() => {
        const items = {};

        if (relatedProject) {
            // Add customer info
            if (relatedProject.aaroCustomerName) {
                items['Müşteri'] = relatedProject.aaroCustomerName;
            }

            // Add component counts and amounts - show all even if 0
            Object.keys(componentTotals).forEach((key) => {
                if (componentTotals[key]) {
                    const count = componentTotals[key].count;
                    const amount = componentTotals[key].amount;
                    items[key] = `${count.toLocaleString('tr-TR')} adet (${formatCurrency(amount, currency)})`;
                }
            });

            // Add main total without groups
            if (mainTotal > 0) {
                items['Bileşenler Toplamı'] = (
                    <MDTypography variant="button" fontWeight="bold" color="primary">
                        {formatCurrency(mainTotal, currency)}
                    </MDTypography>
                );

                // Add VAT-included components total
                items['Bileşenler Toplamı +(%20)'] = (
                    <MDTypography variant="button" fontWeight="bold" color="primary">
                        {formatCurrency(mainTotal * 1.2, currency)}
                    </MDTypography>
                );
            }

            // Add group totals
            Object.keys(groupTotals).forEach((groupId, index) => {
                const group = groupTotals[groupId];
                items[`Grup ${index + 1}`] = `${formatCurrency(group.amount, currency)} (${group.name})`;
            });

            // Add groups total if there are groups
            if (groupsTotal > 0) {
                items['Gruplar Toplamı'] = (
                    <MDTypography variant="button" fontWeight="bold" color="success">
                        {formatCurrency(groupsTotal, currency)}
                    </MDTypography>
                );

                // Add VAT-included groups total
                items['Gruplar Toplamı +(%20)'] = (
                    <MDTypography variant="button" fontWeight="bold" color="success">
                        {formatCurrency(groupsTotal * 1.2, currency)}
                    </MDTypography>
                );
            }
        }

        return items;
    }, [relatedProject, componentTotals, mainTotal, currency, groupTotals, groupsTotal]);

    // If in dialog mode, automatically open the associate dialog
    useEffect(() => {
        if (open && onClose) {
            setOpenAssociateDialog(true);
        }
    }, [open, onClose]);

    // Handle dialog open for normal mode
    const handleOpenAssociateDialog = useCallback(() => {
        setOpenAssociateDialog(true);
        setSearchQuery('');
        setTimeout(() => {
            refetchProjects();
        }, 100);
    }, []);

    // Handle dialog close in dialog mode
    const handleCloseAssociateDialog = useCallback(() => {
        setOpenAssociateDialog(false);
        setSearchQuery('');
        setSearchResults([]);

        // If in dialog mode, also close the parent dialog
        if (onClose) {
            onClose();
        }
    }, [onClose]);

    // Handle project search
    const handleSearchProjects = useCallback(() => {
        setIsSearching(true);
        setTimeout(() => {
            refetchProjects();
        }, 50);
    }, [debouncedSearchQuery]);

    // Search projects query
    const {
        data: projectsData,
        isLoading: projectsLoading,
        refetch: refetchProjects,
    } = useQuery({
        queryKey: ['searchProjectsInProductionRelation', debouncedSearchQuery, page, limit],
        queryFn: async () => {
            try {
                // Create search parameters - replace empty strings with undefined
                const params = {
                    searchQuery: debouncedSearchQuery || undefined,
                    page,
                    limit,
                    isActive: true,
                };

                // Require at least 2 characters for search
                if (debouncedSearchQuery && debouncedSearchQuery.length < 2) {
                    const response = await searchProjects({
                        isActive: true,
                        page: 1,
                        limit: 10,
                    });
                    return response.data;
                }

                const response = await searchProjects(params);
                return response.data;
            } catch (error) {
                console.error('Error searching projects:', error);
                throw error;
            } finally {
                // Ensure loading state is turned off when query completes
                setIsSearching(false);
            }
        },
        enabled: false, // Manually triggered
        keepPreviousData: true,
        refetchOnWindowFocus: false,
        retry: 1,
        onSuccess: () => setIsSearching(false),
        onError: () => setIsSearching(false),
    });

    // Update search results when data changes
    useEffect(() => {
        if (projectsData?.result) {
            setSearchResults(projectsData.result);
            setIsSearching(false);
        } else if (projectsData) {
            setSearchResults([]);
            setIsSearching(false);
        }
    }, [projectsData]);

    // Show activity indicator during search
    useEffect(() => {
        if (openAssociateDialog && searchQuery !== debouncedSearchQuery) {
            setIsSearching(true);
        }
    }, [searchQuery, debouncedSearchQuery, openAssociateDialog]);

    // Trigger search when debounced query changes
    useEffect(() => {
        if (openAssociateDialog) {
            handleSearchProjects();
        }
    }, [debouncedSearchQuery, openAssociateDialog, handleSearchProjects]);

    // Handle project association
    const handleAssociateWithProject = async (projectId) => {
        try {
            const project = searchResults.find((p) => (p._id || p.id) === projectId);
            const projectName = project ? project.projectName || 'Seçilen proje' : 'Proje';

            setIsUpdating(true);

            // Update the production with the selected project
            const response = await updateProduction(production._id, {
                project: projectId,
            });

            if (response.data && response.data.success) {
                alert(`Üretim başarıyla "${projectName}" ile eşleştirildi.`);
                // Refresh the page to show the updated association
                window.location.reload();
                handleCloseAssociateDialog();
            } else {
                throw new Error(response.data?.message || 'Eşleştirme sırasında bir hata oluştu');
            }
        } catch (error) {
            console.error('Eşleştirme hatası:', error);
            alert(`Eşleştirme hatası: ${error.message}`);
        } finally {
            setIsUpdating(false);
        }
    };

    // Function to render the associate dialog to avoid code duplication
    const renderAssociateDialog = () => (
        <Dialog
            open={openAssociateDialog}
            onClose={handleCloseAssociateDialog}
            fullWidth
            maxWidth="sm"
            PaperProps={{
                sx: { borderRadius: '10px' },
            }}
        >
            <DialogTitle>
                <MDBox display="flex" justifyContent="space-between" alignItems="center">
                    <MDTypography variant="h6">Üretimi Proje ile İlişkilendir</MDTypography>
                    <IconButton onClick={handleCloseAssociateDialog} size="small">
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </MDBox>
            </DialogTitle>
            <DialogContent dividers>
                <MDBox mb={2}>
                    <TextField
                        fullWidth
                        label="Proje veya Müşteri Adı"
                        variant="outlined"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="Proje veya müşteri adıyla ara..."
                        slotProps={{
                            input: {
                                endAdornment: searchQuery !== debouncedSearchQuery && (
                                    <InputAdornment position="end">
                                        <CircularProgress size={20} />
                                    </InputAdornment>
                                ),
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon />
                                    </InputAdornment>
                                ),
                            },
                        }}
                    />
                </MDBox>

                {isSearching || projectsLoading ? (
                    <MDBox display="flex" justifyContent="center" py={3}>
                        <CircularProgress size={30} />
                    </MDBox>
                ) : searchResults.length > 0 ? (
                    <List sx={{ maxHeight: '300px', overflow: 'auto' }}>
                        {searchResults.map((project) => (
                            <ListItem
                                key={project._id || project.id}
                                divider
                                sx={{
                                    borderRadius: '8px',
                                    mb: 1,
                                    '&:hover': { bgcolor: 'grey.100' },
                                }}
                                secondaryAction={
                                    <MDButton
                                        variant="gradient"
                                        color="success"
                                        size="small"
                                        onClick={() => handleAssociateWithProject(project._id || project.id)}
                                        disabled={isUpdating}
                                    >
                                        {isUpdating ? <CircularProgress size={20} /> : 'İlişkilendir'}
                                    </MDButton>
                                }
                            >
                                <ListItemText
                                    primary={
                                        <MDTypography variant="button" fontWeight="medium">
                                            {project.projectName || 'İsimsiz Proje'}
                                        </MDTypography>
                                    }
                                    secondary={
                                        <MDTypography variant="caption" display="block">
                                            Müşteri:{' '}
                                            {project.aaroCustomerName || project.customer?.name || 'Belirtilmemiş'}
                                        </MDTypography>
                                    }
                                />
                            </ListItem>
                        ))}
                    </List>
                ) : (
                    <MDBox display="flex" flexDirection="column" justifyContent="center" alignItems="center" py={3}>
                        <SearchIcon color="disabled" style={{ fontSize: 48, marginBottom: 16 }} />
                        <MDTypography variant="body2" color="text" textAlign="center">
                            {searchQuery
                                ? 'Arama sonucu bulunamadı.'
                                : 'Proje veya müşteri adı ile arama yapabilirsiniz.'}
                        </MDTypography>
                    </MDBox>
                )}
            </DialogContent>
            <DialogActions>
                <MDButton variant="text" color="dark" onClick={handleCloseAssociateDialog}>
                    İptal
                </MDButton>
            </DialogActions>
        </Dialog>
    );

    // If no project is related, show a simple card with a button to associate
    if (!relatedProject) {
        return (
            <MDBox mb={3}>
                <MDBox
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    p={2}
                    border="1px solid"
                    borderColor="grey.200"
                    borderRadius="lg"
                    bgcolor="white"
                >
                    <MDTypography variant="button" color="text">
                        Proje ile ilişkilendirilmemiş, ayarlardan ilişkilendirme yapınız.
                    </MDTypography>
                    {showRelateButton && (
                        <MDButton
                            variant="contained"
                            color="info"
                            size="small"
                            startIcon={<AddIcon />}
                            onClick={handleOpenAssociateDialog}
                        >
                            Proje İlişkilendir
                        </MDButton>
                    )}
                </MDBox>

                {renderAssociateDialog()}
            </MDBox>
        );
    }

    // When project is related, show detailed project info with SummaryCard
    return (
        <MDBox mb={3}>
            <SummaryCard
                title="İlişkili Proje"
                subtitle={
                    <MDBox display="flex" alignItems="center" onClick={() => setValue(2)}>
                        {relatedProject.projectName}
                    </MDBox>
                }
                items={summaryItems}
                customIcon={
                    <EditIcon fontSize="small" onClick={handleOpenAssociateDialog} style={{ cursor: 'pointer' }} />
                }
                showPrintIcon={Object.keys(summaryItems).length > 0}
                shadow={false}
            />

            {renderAssociateDialog()}
        </MDBox>
    );
}

export default ProjectsSection;
